import { QueryClient } from '@tanstack/react-query';

import { ApiError, RateLimitError } from '@/lib/api';

/**
 * Default QueryClient config.  Tuned for a doc-heavy app where re-fetching
 * is cheap (backend is fast; users don't navigate rapidly between the
 * same resource twice).
 *
 * Mutations never retry automatically — surface the error and let the
 * component decide.  Queries retry only on transient 5xx / network errors.
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000, // 30s — usage counters + plan state are fresh enough
        gcTime: 5 * 60_000, // 5 min cache retention
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // 429 — retry once, honouring the backend's retry-after hint.
          if (error instanceof RateLimitError) return failureCount < 1;
          // Any other 4xx is a client error the user must act on — auth,
          // plan gate, forbidden / feature_not_assigned, validation,
          // not-found, conflict, gone, payload-too-large. None are
          // transient, so never retry (prevents 401/403/4xx retry storms).
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            return false;
          }
          // 5xx + network errors are transient — retry with backoff.
          return failureCount < 2;
        },
        // Honour the backend's retry_after_seconds hint for 429s (F4-03).
        // Everything else uses capped exponential backoff.
        retryDelay: (count, error) =>
          error instanceof RateLimitError ? error.retryAfterMs : Math.min(1000 * 2 ** count, 8_000),
      },
      mutations: {
        retry: false, // apiFetch already handles 429 / 5xx internally
      },
    },
  });
}
